import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef } from 'react';
import type { LatestNewsProps, LatestNewsRef } from './LatestNews.data';
import styles from './LatestNews.module.scss';
import Heading from '../../atoms/heading/Heading';
import { useGlobalStore } from '../../../stores/global';
import ArticlePreview from './article-preview/ArticlePreview';
import NewsOverviewItem from '../news-overview/news-overview-item/NewsOverviewItem';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';

export default function LatestNews({
  className,
  canTransitionIn,
  blok,
  ref,
}: LatestNewsProps & { ref?: Ref<LatestNewsRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const store = useGlobalStore();
  const { recentNews } = store;
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, false);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.latestNews, className, {
        [styles.fullbleed]: ['fullbleed', 'center'].includes(blok.layout),
        [styles.center]: blok.layout === 'center',
      })}
      {...sbEditable(blok)}
    >
      <Heading
        as="h4"
        size="heading-04"
        className={styles.title}
        disableTriggerInOnScroll={false}
        canTransitionIn={canTransitionIn}
      >
        {blok.title}
      </Heading>

      <div className={styles.previewsContainer}>
        {recentNews &&
          recentNews
            ?.slice(0, blok.per_page)
            .map(({ content, published_at, full_slug }, index) =>
              blok.layout === 'center' ? (
                <ArticlePreview
                  key={full_slug}
                  blok={{ ...content }}
                  full_slug={full_slug}
                  layout={blok.layout}
                  canTransitionIn={canTransitionIn}
                  className={classNames(styles.item)}
                  date={content.publish_date || (published_at as string)}
                  textOnly={blok.text_only}
                />
              ) : (
                <NewsOverviewItem
                  transitionInDelay={isMobileViewport ? 0 : (index % 3) * 0.15}
                  className={classNames(styles.item)}
                  blok={{ ...content }}
                  full_slug={full_slug}
                  key={`${full_slug}`}
                  textOnly={blok.text_only}
                />
              ),
            )}
      </div>
    </div>
  );
}
